import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listSentPendingSharesHousesHouseIdDocumentsPendingSentGetOptions } from '@/client/@tanstack/react-query.gen';
import { PendingDocumentShareResponse } from '@/client/types.gen';
import { selectPostTransferDocs } from '@/utils/documentShareUtils';
import { UploadedRecord } from '@/hooks/house/documents/useUploadedRecords';

/**
 * Hook to detect documents that should be shared with the owner after a transfer.
 * Returns the list of pending documents and loading state.
 */
export const usePendingDocumentsToShare = (
	houseId: number,
	records: UploadedRecord[],
	lastTransferAt: string | null,
	isEnabled: boolean,
) => {
	// Query pending shares to detect documents already in the sharing queue
	const { data: pendingShares = [], isLoading } = useQuery({
		...listSentPendingSharesHousesHouseIdDocumentsPendingSentGetOptions({
			path: { house_id: houseId },
		}),
		enabled: isEnabled,
	});

	// Document ids already in the sharing queue (both the exclusion filter below and callers use this).
	const sentDocumentIds = useMemo(
		() =>
			new Set(
				(pendingShares as PendingDocumentShareResponse[])
					.map((p) => p.document_id)
					.filter((id): id is number => typeof id === 'number'),
			),
		[pendingShares],
	);

	// Compute the list of documents that should be shared
	const pendingDocsToShare = useMemo(() => {
		if (!isEnabled || !lastTransferAt) {
			return [];
		}

		// Get documents uploaded after transfer, keyed by id so the filter below is an O(1) lookup
		// per record rather than a nested scan.
		const postTransferDocIds = new Set(selectPostTransferDocs(records, lastTransferAt).map((d) => d.id));

		// Filter out documents already pending
		return records.filter(
			(record) =>
				record.document &&
				postTransferDocIds.has(record.document.id) &&
				!sentDocumentIds.has(record.document.id),
		);
	}, [isEnabled, lastTransferAt, records, sentDocumentIds]);

	return {
		pendingDocsToShare,
		sentDocumentIds,
		isPendingSharesLoading: isLoading,
	};
};
